import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Select, { components } from "react-select";
import { ReactComponent as ScheduleIcon } from "../../assets/img/icons/schedule.svg";
import { selectAll as selectAllBoards } from '../../redux/slices/boards.slice';
import { selectAll as selectAllStudents, studentsResetConflicts } from '../../redux/slices/students.slice';
import { addChosenBoard } from '../../redux/slices/boards.slice.js';
import Table from "react-bootstrap/Table";
import { Button, Container, Row, Col } from "react-bootstrap";
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import styles from './index.module.scss';
import './index.scss';
import { calculateAge } from '../../utilities';
import { filterStudentsByName } from './studentSearch';
import axios from 'axios';
import { fetchDancerSchedulePdf } from './dancerExport';

const Control = ({ children, ...props }) => {
    return (
        <components.Control {...props}>
            <ScheduleIcon className="ms-2" />
            {children}
        </components.Control>
    );
};

const formatTimeValue = (value) => {
    if (!value) return '';
    const parsed = moment(value, ['HH:mm:ss', 'HH:mm'], true);
    return parsed.isValid() ? parsed.format('HH:mm') : value;
};

const formatTimeRange = (start, end) => {
    const s = formatTimeValue(start);
    const e = formatTimeValue(end);
    if (s && e) return `${s} - ${e}`;
    return s || e || '—';
};

// Sort indicator shown in column headers
const SortIndicator = ({ field, sortField, sortDir }) => {
    if (sortField !== field) {
        return <span style={{ opacity: 0.3, marginLeft: '4px', fontSize: '11px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: '#4361ee' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

// The only part of the download a jest test cannot exercise: jsdom has no object
// URLs and no downloads. Everything that can be tested lives in dancerExport.js
// instead, so this stays four lines with no rules in it.
const saveBlobAsFile = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
};

const SORTABLE_COLUMNS = ['name', 'birthDate', 'age', 'scheduledCount', 'regularCount'];

const StudentsTab = ({ onJumpToClass }) => {
    const { isAdmin, id } = useSelector(state => state.personality.user);
    const boards = useSelector(selectAllBoards);
    const students = useSelector(selectAllStudents);
    const { loading } = useSelector(state => state.status);
    const conflictsState = useSelector(state => ({
        conflicts: state.students.conflicts,
        conflictsLoading: state.students.conflictsLoading,
        conflictsCheckedAt: state.students.conflictsCheckedAt,
    }));
    const params = useParams();
    const dispatch = useDispatch();
    const previousBoardIdRef = useRef(null);
    const { chosenBoard } = useSelector(state => state.boards);

    const [board, setBoard] = useState(null);
    const [boardOptions, setBoardOptions] = useState([]);
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [searchQuery, setSearchQuery] = useState('');
    const [exportingStudentId, setExportingStudentId] = useState(null);

    const userId = isAdmin ? params.id : id;

    const handleSort = useCallback((field) => {
        setSortField(prev => {
            if (prev === field) {
                setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                return field;
            }
            setSortDir('asc');
            return field;
        });
    }, []);

    useEffect(() => {
        if (boards && boards.length > 0) {
            const options = boards.map((item) => ({
                value: item.id,
                label: item.boardName,
                id: item.id,
                boardName: item.boardName,
                ...item
            }));
            setBoardOptions(options);

            if (options.length > 0) {
                let selectedBoard = null;
                if (chosenBoard && chosenBoard.id) {
                    selectedBoard = options.find(opt => opt.id === chosenBoard.id) || options[0];
                } else {
                    selectedBoard = options[0];
                }
                if (!board || board.id !== selectedBoard.id) setBoard(selectedBoard);
                if (!chosenBoard || chosenBoard.id !== selectedBoard.id) dispatch(addChosenBoard(selectedBoard));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boards, dispatch, chosenBoard]);

    useEffect(() => {
        if (board && userId) {
            const boardId = board.value || board.id;
            if (boardId) dispatch({ type: "STUDENTS_GET_ALL", query: { userId, boardId } });
        }
    }, [board, userId, dispatch]);

    useEffect(() => {
        const currentBoardId = board ? (board.value || board.id) : null;
        if (previousBoardIdRef.current === currentBoardId) return;
        previousBoardIdRef.current = currentBoardId;
        dispatch(studentsResetConflicts());
    }, [board, dispatch]);

    const filteredStudents = useMemo(() => {
        if (!board || !students) return [];
        const boardId = board.value || board.id;
        return students.filter(student => student.boardId === boardId);
    }, [students, board]);

    // Name search typed in the toolbar. Empty query = every student on the board.
    const searchedStudents = useMemo(
        () => filterStudentsByName(filteredStudents, searchQuery),
        [filteredStudents, searchQuery]
    );

    const studentsWithDerivedData = useMemo(() => {
        const withDerived = searchedStudents.map((student) => {
            const age = calculateAge(student.birthDate);
            const formattedBirthDate = student.birthDate
                ? moment(student.birthDate).format('MM/DD/YYYY')
                : '—';
            return {
                ...student,
                derivedAge: age,
                formattedBirthDate,
                scheduleStickers: student.scheduleStickers || [],
                regularStickers: student.regularStickers || [],
            };
        });

        return [...withDerived].sort((a, b) => {
            let valA, valB;
            switch (sortField) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'birthDate':
                    valA = a.birthDate ? new Date(a.birthDate).getTime() : 0;
                    valB = b.birthDate ? new Date(b.birthDate).getTime() : 0;
                    break;
                case 'age':
                    valA = a.derivedAge ?? -1;
                    valB = b.derivedAge ?? -1;
                    break;
                case 'scheduledCount':
                    valA = a.scheduleStickers.length;
                    valB = b.scheduleStickers.length;
                    break;
                case 'regularCount':
                    valA = a.regularStickers.length;
                    valB = b.regularStickers.length;
                    break;
                default:
                    return 0;
            }
            if (valA < valB) return sortDir === 'asc' ? -1 : 1;
            if (valA > valB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [searchedStudents, sortField, sortDir]);

    const handleBoardChange = (selectedBoard) => {
        setBoard(selectedBoard);
        dispatch(addChosenBoard(selectedBoard));
    };

    const handleCheckConflicts = useCallback(() => {
        if (!board || !userId) return;
        const boardId = board.value || board.id;
        if (!boardId) return;
        dispatch({ type: "STUDENTS_CHECK_CONFLICTS", query: { userId, boardId } });
    }, [board, userId, dispatch]);

    // One dancer's week as a one-page PDF. The studio is not sent: the route reads
    // it from the signed-in token, and the whole reason that route exists separately
    // from the four schedule exports is that it refuses to trust a query string.
    const handleExportDancerSchedule = useCallback(async (student) => {
        const boardId = board ? (board.value || board.id) : null;
        if (!student || !boardId) return;
        setExportingStudentId(student.id);
        try {
            const { blob, filename } = await fetchDancerSchedulePdf({
                httpGet: axios.get,
                boardId,
                studentId: student.id,
                dancerName: student.name,
                accessToken: localStorage.getItem('accessToken'),
            });
            saveBlobAsFile(blob, filename);
        } catch (err) {
            window.alert(err.message);
        } finally {
            setExportingStudentId(null);
        }
    }, [board]);

    const handleDeleteStudent = useCallback((student) => {
        if (!student) return;
        if (!window.confirm(`Delete student "${student.name}" and remove all associations?`)) return;
        dispatch({ type: "STUDENT_REMOVE_ONE", payload: { studentId: student.id, userId } });
    }, [dispatch, userId]);

    const thStyle = (field) => ({
        cursor: SORTABLE_COLUMNS.includes(field) ? 'pointer' : 'default',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    });

    return (
        <Container fluid className={styles.pageWrapper}>
            <Row className={styles.fullHeightRow}>
                <Col className={`${styles.fullHeightCol} ps-0`} sm={12}>
                    <div className={styles.tableContainer}>
                        <div className={styles.selectContainer}>
                            <div className={styles.actionsRow}>
                                <div className={styles.selectWrapper}>
                                    <Select
                                        components={{ Control }}
                                        className="schedulesSelectButton my-1 mx-1"
                                        placeholder={"Schedules"}
                                        classNamePrefix="schedulesSelect"
                                        value={board}
                                        onChange={handleBoardChange}
                                        options={boardOptions}
                                    />
                                </div>
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="search"
                                        className={styles.searchInput}
                                        placeholder="Search dancers by name"
                                        aria-label="Search dancers by name"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        disabled={!board}
                                    />
                                    {searchQuery.trim() ? (
                                        <span className={styles.searchCount}>
                                            {searchedStudents.length} of {filteredStudents.length}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={styles.actionsRight}>
                                    <button
                                        type="button"
                                        className={styles.checkButton}
                                        onClick={handleCheckConflicts}
                                        disabled={!board || conflictsState.conflictsLoading}
                                    >
                                        {conflictsState.conflictsLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm"
                                                    role="status" aria-hidden="true" className="me-2" />
                                                Checking...
                                            </>
                                        ) : (
                                            'Check for conflicts'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className={styles.contentWrapper}>
                            <div className={styles.tableColumn}>
                                {loading ? (
                                    <div className={styles.spinnerWrapper}>
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    </div>
                                ) : (
                                    <div className={styles.tableScroll}>
                                        <Table className={styles.table} hover striped={false}>
                                            <thead>
                                                <tr>
                                                    <th style={thStyle('name')} onClick={() => handleSort('name')}>
                                                        Name <SortIndicator field="name" sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                    <th style={thStyle('birthDate')} onClick={() => handleSort('birthDate')}>
                                                        Birth Date <SortIndicator field="birthDate" sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                    <th style={thStyle('age')} onClick={() => handleSort('age')}>
                                                        Age <SortIndicator field="age" sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                    <th style={thStyle('scheduledCount')} onClick={() => handleSort('scheduledCount')}>
                                                        Scheduled Classes <SortIndicator field="scheduledCount" sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                    <th style={thStyle('regularCount')} onClick={() => handleSort('regularCount')}>
                                                        {/* Classes from the Class library the dancer is in that have
                                                            not been placed on this schedule yet. Was "Regular Classes",
                                                            which nobody could read. Andrew, 2026-10-09. */}
                                                        Classes not on schedule <SortIndicator field="regularCount" sortField={sortField} sortDir={sortDir} />
                                                    </th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {studentsWithDerivedData.length > 0 ? (
                                                    studentsWithDerivedData.map((student) => (
                                                        <tr key={student.id}>
                                                            <td>{student.name}</td>
                                                            <td>{student.formattedBirthDate}</td>
                                                            <td>{student.derivedAge ?? '—'}</td>
                                                            <td>
                                                                {student.scheduleStickers.length > 0
                                                                    ? student.scheduleStickers.map((s, i) => (
                                                                        <span key={s.id}>
                                                                            {s.className}{i < student.scheduleStickers.length - 1 ? ', ' : ''}
                                                                        </span>
                                                                    ))
                                                                    : 'None'}
                                                            </td>
                                                            <td>
                                                                {student.regularStickers.length > 0
                                                                    ? student.regularStickers.map((s, i) => (
                                                                        <span key={s.id}>
                                                                            {s.className}{i < student.regularStickers.length - 1 ? ', ' : ''}
                                                                        </span>
                                                                    ))
                                                                    : 'None'}
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    variant="link"
                                                                    className={styles.exportButton}
                                                                    onClick={() => handleExportDancerSchedule(student)}
                                                                    disabled={exportingStudentId === student.id}
                                                                    title={`Download ${student.name || 'this dancer'}'s week as a one-page PDF`}
                                                                >
                                                                    {exportingStudentId === student.id ? 'Preparing...' : 'Schedule PDF'}
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    className={styles.deleteButton}
                                                                    onClick={() => handleDeleteStudent(student)}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className="text-center">
                                                            No students found for this board
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                            <div className={styles.conflictsSection}>
                                <div className={styles.conflictsHeader}>
                                    <h5 className="mb-0">Schedule conflicts</h5>
                                    {conflictsState.conflictsCheckedAt && (
                                        <span className={styles.conflictsTimestamp}>
                                            Checked {moment(conflictsState.conflictsCheckedAt).format('DD.MM.YYYY HH:mm')}
                                        </span>
                                    )}
                                </div>
                                {conflictsState.conflictsLoading ? (
                                    <div className={styles.spinnerWrapper}>
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Checking...</span>
                                        </Spinner>
                                    </div>
                                ) : conflictsState.conflictsCheckedAt ? (
                                    conflictsState.conflicts && conflictsState.conflicts.length > 0 ? (
                                        conflictsState.conflicts.map((item) => (
                                            <div key={item.id} className={styles.conflictCard}>
                                                <div className={styles.conflictStudent}>
                                                    <strong>{item.name}</strong>
                                                </div>
                                                <ul className={styles.conflictList}>
                                                    {item.conflicts.map(conflict => (
                                                        <li
                                                            key={`${item.id}-${conflict.stickerId}`}
                                                            className={onJumpToClass ? styles.conflictItemClickable : ''}
                                                            onClick={() => onJumpToClass && onJumpToClass({
                                                                id: conflict.stickerId,
                                                                dayOfTheWeek: conflict.dayOfTheWeek,
                                                                start: conflict.start,
                                                            })}
                                                            title={onJumpToClass ? 'Click to jump to this class on the schedule' : undefined}
                                                        >
                                                            <span className={styles.conflictClass}>{conflict.className || 'Class'}</span>
                                                            <span className={styles.conflictMeta}>
                                                                {conflict.dayOfTheWeek || 'Day not set'}, {formatTimeRange(conflict.start, conflict.end)}
                                                            </span>
                                                            {onJumpToClass && <span className={styles.jumpHint}>→ Go to schedule</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))
                                    ) : (
                                        <p className={styles.conflictsEmpty}>
                                            No overlapping students were found for this board.
                                        </p>
                                    )
                                ) : (
                                    <p className={styles.conflictsEmpty}>
                                        Click "Check" to find students that attend overlapping classes.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default StudentsTab;
